import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import './WindDifferentialField.css';

/**
 * Get color for wind speed (professional meteorological colors)
 * Uses a vibrant gradient from teal (calm) to magenta (strong)
 */
const getWindSpeedColor = (speed, maxSpeed = 25) => {
  const normalized = Math.min(speed / maxSpeed, 1);
  
  // Professional meteorological gradient: teal → cyan → blue → purple → magenta
  if (normalized < 0.15) {
    // Calm: teal
    return '#14b8a6';
  } else if (normalized < 0.3) {
    // Light wind: cyan
    const t = (normalized - 0.15) / 0.15;
    return lerpColor('#14b8a6', '#22d3ee', t);
  } else if (normalized < 0.45) {
    // Moderate: cyan to blue
    const t = (normalized - 0.3) / 0.15;
    return lerpColor('#22d3ee', '#3b82f6', t);
  } else if (normalized < 0.6) {
    // Moderate-strong: blue
    const t = (normalized - 0.45) / 0.15;
    return lerpColor('#3b82f6', '#6366f1', t);
  } else if (normalized < 0.75) {
    // Strong: indigo to purple
    const t = (normalized - 0.6) / 0.15;
    return lerpColor('#6366f1', '#8b5cf6', t);
  } else if (normalized < 0.9) {
    // Very strong: purple to pink
    const t = (normalized - 0.75) / 0.15;
    return lerpColor('#8b5cf6', '#ec4899', t);
  } else {
    // Extreme: pink to magenta
    const t = (normalized - 0.9) / 0.1;
    return lerpColor('#ec4899', '#db2777', t);
  }
};

/**
 * Linear interpolation between two hex colors
 */
const lerpColor = (color1, color2, t) => {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);
  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const b = Math.round(c1.b + (c2.b - c1.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Convert hex color to RGB object
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

/**
 * Create an elegant streamlined arrow icon for wind direction
 * Color-coded by wind speed with glow effect
 * Arrow points in the direction the wind is blowing TO
 */
const createFieldArrowIcon = (direction, speed, maxSpeed = 25) => {
  // Calculate arrow length based on speed (normalized to maxSpeed)
  const normalizedSpeed = Math.min(speed / maxSpeed, 1);
  const arrowLength = 22 + (normalizedSpeed * 20); // 22-42 pixels
  
  // Get color based on wind speed
  const color = getWindSpeedColor(speed, maxSpeed);
  
  // Dynamic stroke width based on speed
  const strokeWidth = 2.5 + (normalizedSpeed * 1.5); // 2.5-4px
  
  // Arrow head proportions
  const arrowHeadWidth = 7 + (normalizedSpeed * 3); // 7-10px
  const arrowHeadLength = 8 + (normalizedSpeed * 4); // 8-12px
  
  // Glow intensity based on speed
  const glowOpacity = 0.3 + (normalizedSpeed * 0.3);
  
  // Create elegant arrow SVG with glow effect
  const svg = `
    <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow-${direction}-${speed}" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="arrowGrad-${direction}-${speed}" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" style="stop-color:${color};stop-opacity:0.6" />
          <stop offset="100%" style="stop-color:${color};stop-opacity:1" />
        </linearGradient>
      </defs>
      <g transform="translate(30,30) rotate(${direction})" filter="url(#glow-${direction}-${speed})">
        <!-- Glow layer -->
        <line x1="0" y1="4" x2="0" y2="-${arrowLength - arrowHeadLength + 2}" 
              stroke="${color}" stroke-width="${strokeWidth + 3}" 
              stroke-linecap="round" opacity="${glowOpacity}" />
        <!-- Main arrow shaft with gradient -->
        <line x1="0" y1="4" x2="0" y2="-${arrowLength - arrowHeadLength + 2}" 
              stroke="url(#arrowGrad-${direction}-${speed})" stroke-width="${strokeWidth}" 
              stroke-linecap="round" />
        <!-- Elegant arrowhead -->
        <path d="M0,-${arrowLength} 
                 L-${arrowHeadWidth},-${arrowLength - arrowHeadLength} 
                 Q0,-${arrowLength - arrowHeadLength * 0.5} ${arrowHeadWidth},-${arrowLength - arrowHeadLength} 
                 Z" 
              fill="${color}" stroke="none" />
        <!-- Small dot at base -->
        <circle cx="0" cy="4" r="${1.5 + normalizedSpeed}" fill="${color}" opacity="0.8" />
      </g>
    </svg>
  `;
  
  return L.divIcon({
    html: svg,
    className: 'wind-field-arrow',
    iconSize: [60, 60],
    iconAnchor: [30, 30]
  });
};

export function WindDifferentialField({ stations, visible = false, farm }) {
  const map = useMap();
  const markersRef = useRef([]);

  useEffect(() => {
    if (!visible || !stations || stations.length === 0 || !farm) {
      // Remove all markers
      markersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      markersRef.current = [];
      return;
    }

    // Get map bounds
    const bounds = map.getBounds();
    const mapBounds = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    };

    // Generate wind vectors ONLY at station locations (actual measurement points)
    const stationWindData = [];
    
    stations.forEach(station => {
      if (!station.currentReadings || !station.isActive) return;
      
      const windSpeed = station.currentReadings.windSpeed || 0;
      if (windSpeed <= 0) return;
      
      // Get wind direction from station or calculate from location
      let windDirection = 0;
      if (station.currentReadings.windDirection !== undefined) {
        windDirection = station.currentReadings.windDirection;
      } else {
        // Fallback: use location-based pattern
        const baseDirection = (station.locationLat * 100 + station.locationLon * 100) % 360;
        windDirection = baseDirection;
      }
      
      stationWindData.push({
        lat: station.locationLat,
        lon: station.locationLon,
        direction: windDirection,
        speed: windSpeed
      });
    });

    if (stationWindData.length === 0) {
      markersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      markersRef.current = [];
      return;
    }

    // Find max wind speed for normalization
    const maxSpeed = Math.max(
      ...stationWindData.map(p => p.speed),
      25 // Default max
    );

    // Create markers ONLY at station locations
    const newMarkers = [];
    stationWindData.forEach((point) => {
      const icon = createFieldArrowIcon(point.direction, point.speed, maxSpeed);
      
      const marker = L.marker(
        [point.lat, point.lon],
        { icon, zIndexOffset: 1100, interactive: false } // Higher z-index to appear above dropdown
      );
      
      marker.addTo(map);
      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;

    // Update when map moves or zooms - with throttling for performance
    let updateTimeout = null;
    let isUpdating = false;
    
    const updateField = () => {
      // Skip if already updating
      if (isUpdating) return;
      
      // Clear any pending updates
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      
      // OPTIMIZED: Throttle updates more aggressively for better performance
      updateTimeout = setTimeout(() => {
        isUpdating = true;
        
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
        // Remove old markers
        markersRef.current.forEach(marker => {
          map.removeLayer(marker);
        });
        markersRef.current = [];

        // Get new bounds
        const newBounds = map.getBounds();
        const newMapBounds = {
          north: newBounds.getNorth(),
          south: newBounds.getSouth(),
          east: newBounds.getEast(),
          west: newBounds.getWest()
        };

        // Generate wind vectors ONLY at station locations
        const newStationWindData = [];
        
        stations.forEach(station => {
          if (!station.currentReadings || !station.isActive) return;
          
          const windSpeed = station.currentReadings.windSpeed || 0;
          if (windSpeed <= 0) return;
          
          // Get wind direction from station or calculate from location
          let windDirection = 0;
          if (station.currentReadings.windDirection !== undefined) {
            windDirection = station.currentReadings.windDirection;
          } else {
            // Fallback: use location-based pattern
            const baseDirection = (station.locationLat * 100 + station.locationLon * 100) % 360;
            windDirection = baseDirection;
          }
          
          newStationWindData.push({
            lat: station.locationLat,
            lon: station.locationLon,
            direction: windDirection,
            speed: windSpeed
          });
        });
        
        if (newStationWindData.length === 0) {
          isUpdating = false;
          return;
        }
        
        const newMaxSpeed = Math.max(...newStationWindData.map(p => p.speed), 25);

        // Create new markers ONLY at station locations
        newStationWindData.forEach((point) => {
          const icon = createFieldArrowIcon(point.direction, point.speed, newMaxSpeed);
          const marker = L.marker(
            [point.lat, point.lon],
            { icon, zIndexOffset: 1100, interactive: false } // Higher z-index to appear above dropdown
          );
          marker.addTo(map);
          markersRef.current.push(marker);
        });
        
        isUpdating = false;
        }); // Close requestAnimationFrame
      }, 600); // OPTIMIZED: Increased delay to 600ms for better performance
    };

    map.on('moveend', updateField);
    map.on('zoomend', updateField);

    // Cleanup
    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      map.off('moveend', updateField);
      map.off('zoomend', updateField);
      markersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      markersRef.current = [];
    };
  }, [map, stations, visible, farm]);

  return null;
}

